import { useContext, useEffect, useState } from 'react';
import SchemaForm from '@/components/_common/SchemaForm/SchemaForm';
import { selectActiveVersion } from '@/redux/selectors/titleSelectors';
import { isActiveVersionAVersion } from '@/redux/slices/slice_types';
import { Box, CircularProgress, Grid, lighten, Typography } from '@mui/material';
import { debounce } from 'lodash';
import TitleDetailMetadataButtons from './TitleDetailMetadataButtons';
import { useAppSelector } from '@/redux/hooks';
import axios from '@/_utils/axios';
import styled from '@emotion/styled';
import { PolarisIcon } from '@/exports/columnCommonComponents';
import { ConfigContext } from '@/contextAPI/configContext';

const TitleDetailMetadataSchema = () => {
  const activeVersion = useAppSelector(selectActiveVersion);
  if (!isActiveVersionAVersion(activeVersion)) return null;

  const [editMode, setEditMode] = useState<boolean>(false);
  const [data, setData] = useState<any>({});
  const [originalData, setOriginalData] = useState<any>({});
  const [selectedLanguage, setSelectedLanguage] = useState<string>('');
  const [fetchingMetadata, setFetchingMetadata] = useState<boolean>(false);
  const [dataLoaded, setDataLoaded] = useState<boolean>(false);
  const [metadataSchema, setMetadataSchema] = useState<any>(null);
  const enableMetadataUpdate = useContext(ConfigContext)?.titleConfig?.enableMetadataUpdate;

  // only set up debounced updates if editing is enabled
  const debouncedSetData = enableMetadataUpdate ? debounce(setData, 600) : undefined;

  const setInitialData = (newData: any, language?: string) => {
    setData(newData);
    setOriginalData(newData);
    setDataLoaded(true);
    if (language) {
      setSelectedLanguage(language);
    }
  };

  const fetchMetadata = (versionId: string | number, language: string): Promise<any> => {
    setFetchingMetadata(true);
    return axios
      .get(`/inventory/titles/${versionId}/metadata/${language}/`)
      .then((res) => {
        if (!res.data) {
          throw new Error('No metadata found');
        } else {
          return res.data?.data;
        }
      })
      .catch((error) => {
        console.error('Error fetching metadata:', error);
        return null;
      })
      .finally(() => setFetchingMetadata(false));
  };

  useEffect(() => {
    return () => {
      debouncedSetData?.cancel();
    };
  }, []);

  useEffect(() => {
    if (!activeVersion?.id) {
      if (dataLoaded) {
        setDataLoaded(false);
        setData({});
        setOriginalData({});
        setMetadataSchema(null);
      }
      return;
    }

    // only fetch if we don't already have data loaded for this version
    if (dataLoaded) {
      return;
    }

    // always fetch metadata from API
    const languageToUse = activeVersion.language || (Array.isArray(activeVersion.metadata_languages) ? activeVersion.metadata_languages[0] : null);
    if (languageToUse && activeVersion.id) {
      setSelectedLanguage(languageToUse);
      fetchMetadata(activeVersion.id, languageToUse).then((fetchedData) => {
        if (fetchedData?.metadata_values) {
          setInitialData(fetchedData.metadata_values, languageToUse);
        }
        if (fetchedData?.metadata_schema) {
          setMetadataSchema(fetchedData.metadata_schema);
        }
        if (!fetchedData) {
          setDataLoaded(true);
        }
      });
      return;
    }

    setDataLoaded(true);
  }, [activeVersion?.id, activeVersion?.language]);

  const handleSubmit = () => {
    axios
      .post(`/inventory/titles/${activeVersion.id}/metadata/${selectedLanguage}/`, { data })
      .then((response) => {
        console.log('Metadata submitted successfully:', response.data);
        // Update the original data to reflect the submitted changes
        setOriginalData(data);
        // Exit edit mode after successful submission
        setEditMode(false);
      })
      .catch((error) => {
        console.error('Error submitting metadata:', error);
      });
  };

  const handleCancel = () => {
    setEditMode(false);
    setData(originalData);
  };

  if (!activeVersion?.id) {
    return null;
  }

  // if metadata updates are disabled, just show a simple read-only view
  if (!enableMetadataUpdate) {
    return (
      <VersionBox>
        <Typography variant="h6" color="primary" sx={{ fontSize: '1.2rem', display: 'flex', mb: 3 }}>
          <PolarisIcon height={26} icon="iconamoon:arrow-bottom-right-3-square-thin" style={{ marginRight: 5 }} />
          <span>{activeVersion.name}</span>
        </Typography>
        {metadataSchema && <SchemaForm schema={metadataSchema} formData={data || {}} readonly formContext={{ rowSpacing: 3, columnSpacing: 4 }} />}
        {!metadataSchema && (
          <Box sx={{ minHeight: '200px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            {fetchingMetadata && <CircularProgress />}
          </Box>
        )}
      </VersionBox>
    );
  }
  
  return (
    <div style={{ position: 'relative' }}>
      <VersionBox style={{ opacity: fetchingMetadata ? 0.5 : 1 }}>
        <Grid container justifyContent="space-between" alignItems="center" sx={{ mb: 3 }}>
          <Typography variant="h6" color="primary" sx={{ fontSize: '1.2rem', display: 'flex' }}>
            <PolarisIcon height={26} icon="iconamoon:arrow-bottom-right-3-square-thin" style={{ marginRight: 5 }} />
            <span>{activeVersion.name}</span>
          </Typography>
          <div>
            <TitleDetailMetadataButtons editMode={editMode} setEditMode={setEditMode} handleSubmit={handleSubmit} handleCancel={handleCancel} />
          </div>
        </Grid>
        {metadataSchema && (
          <SchemaForm
            schema={metadataSchema}
            formData={data || {}}
            readonly={!editMode}
            onChange={(formChangeData) => {
              // only update when editing to prevent initial data override
              if (editMode && debouncedSetData) {
                debouncedSetData(formChangeData.formData);
              }
            }}
            formContext={{ rowSpacing: 3, columnSpacing: 4 }}
          />
        )}
        {!metadataSchema && (
          <Box sx={{ minHeight: '200px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            {fetchingMetadata && <CircularProgress />}
          </Box>
        )}{' '}
        {editMode && metadataSchema && (
          <Box sx={{ mt: 3 }}>
            <TitleDetailMetadataButtons editMode={editMode} setEditMode={setEditMode} handleSubmit={handleSubmit} handleCancel={handleCancel} />
          </Box>
        )}
      </VersionBox>
    </div>
  );
};

export default TitleDetailMetadataSchema;

const VersionBox = styled(Box)`
  margin-top: ${({ theme }) => theme.spacing(3)};
  background: ${({ theme }) => lighten(theme.palette.background.default, 0.03)};
  padding: ${({ theme }) => theme.spacing(3)};
  border-radius: ${({ theme }) => theme.shape.borderRadius}px;
`;
